/**
 * Slider Component
 *
 * A customizable slider/range input component with smooth animations.
 * Follows shadcn/ui patterns for consistent styling.
 *
 * Features:
 * - Draggable track and thumb
 * - Smooth transitions
 * - RTL support
 * - Accessible with keyboard navigation
 * - Touch-friendly
 */

import { type FC, useRef, useState, useCallback, useEffect } from 'react';
import { cn } from '@/utils/cn';

export interface SliderProps {
  /** Current value (0-100 by default) */
  value: number;
  /** Minimum value */
  min?: number;
  /** Maximum value */
  max?: number;
  /** Step increment */
  step?: number;
  /** Callback when value changes */
  onChange?: (value: number) => void;
  /** Callback when user finishes dragging */
  onChangeEnd?: (value: number) => void;
  /** Whether the slider is disabled */
  disabled?: boolean;
  /** Additional CSS classes for the root element */
  className?: string;
  /** Accessible label */
  'aria-label'?: string;
}

export const Slider: FC<SliderProps> = ({
  value,
  min = 0,
  max = 100,
  step = 1,
  onChange,
  onChangeEnd,
  disabled = false,
  className,
  'aria-label': ariaLabel,
}) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [localValue, setLocalValue] = useState(value);

  // Sync local value with prop when not dragging
  useEffect(() => {
    if (!isDragging) {
      setLocalValue(value);
    }
  }, [value, isDragging]);

  // Calculate percentage for positioning
  const percentage = ((localValue - min) / (max - min)) * 100;

  // Calculate value from position
  const getValueFromPosition = useCallback(
    (clientX: number): number => {
      if (!trackRef.current) return localValue;

      const rect = trackRef.current.getBoundingClientRect();
      const isRtl = document.documentElement.dir === 'rtl';

      let percentage: number;
      if (isRtl) {
        percentage = ((rect.right - clientX) / rect.width) * 100;
      } else {
        percentage = ((clientX - rect.left) / rect.width) * 100;
      }

      // Clamp to 0-100
      percentage = Math.max(0, Math.min(100, percentage));

      // Convert to value
      const rawValue = min + (percentage / 100) * (max - min);

      // Round to nearest step
      const steppedValue = Math.round(rawValue / step) * step;

      // Clamp to min/max
      return Math.max(min, Math.min(max, steppedValue));
    },
    [localValue, min, max, step]
  );

  // Handle mouse/touch start
  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (disabled) return;

      e.preventDefault();
      setIsDragging(true);

      const newValue = getValueFromPosition(e.clientX);
      setLocalValue(newValue);
      onChange?.(newValue);

      // Capture pointer for tracking outside element
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
    },
    [disabled, getValueFromPosition, onChange]
  );

  // Handle mouse/touch move
  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isDragging || disabled) return;

      const newValue = getValueFromPosition(e.clientX);
      setLocalValue(newValue);
      onChange?.(newValue);
    },
    [isDragging, disabled, getValueFromPosition, onChange]
  );

  // Handle mouse/touch end
  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (!isDragging) return;

      setIsDragging(false);
      (e.target as HTMLElement).releasePointerCapture(e.pointerId);

      const newValue = getValueFromPosition(e.clientX);
      onChangeEnd?.(newValue);
    },
    [isDragging, getValueFromPosition, onChangeEnd]
  );

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (disabled) return;

      let newValue = localValue;
      const isRtl = document.documentElement.dir === 'rtl';
      const largeStep = step * 10;

      switch (e.key) {
        case 'ArrowLeft':
          newValue = isRtl ? localValue + step : localValue - step;
          break;
        case 'ArrowRight':
          newValue = isRtl ? localValue - step : localValue + step;
          break;
        case 'ArrowUp':
          newValue = localValue + step;
          break;
        case 'ArrowDown':
          newValue = localValue - step;
          break;
        case 'PageUp':
          newValue = localValue + largeStep;
          break;
        case 'PageDown':
          newValue = localValue - largeStep;
          break;
        case 'Home':
          newValue = min;
          break;
        case 'End':
          newValue = max;
          break;
        default:
          return;
      }

      e.preventDefault();
      newValue = Math.max(min, Math.min(max, newValue));
      setLocalValue(newValue);
      onChange?.(newValue);
      onChangeEnd?.(newValue);
    },
    [disabled, localValue, min, max, step, onChange, onChangeEnd]
  );

  return (
    <div
      ref={trackRef}
      className={cn(
        'relative h-2 w-full rounded-full touch-none select-none',
        'bg-surface-200',
        disabled && 'opacity-50 cursor-not-allowed',
        !disabled && 'cursor-pointer',
        className
      )}
      role="slider"
      aria-label={ariaLabel}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={localValue}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onKeyDown={handleKeyDown}
    >
      {/* Filled track - Gold gradient */}
      <div
        className={cn(
          'absolute h-full rounded-full',
          'bg-gradient-to-r from-gold-hero to-gold-warm',
          isDragging && 'from-gold-warm to-gold-hero',
          'transition-colors duration-150'
        )}
        style={{
          width: `${percentage}%`,
          insetInlineStart: 0,
        }}
      />

      {/* Thumb - Gold with glow */}
      <div
        className={cn(
          'absolute top-1/2 -translate-y-1/2',
          'w-4 h-4 rounded-full',
          'bg-gold-hero border-2 border-white',
          'shadow-gold-glow',
          'transition-transform duration-150',
          isDragging && 'scale-125 bg-gold-warm',
          !disabled && 'hover:scale-110',
          'focus-visible:ring-2 focus-visible:ring-gold-hero focus-visible:ring-offset-2'
        )}
        style={{
          insetInlineStart: `calc(${percentage}% - 8px)`,
        }}
      />
    </div>
  );
};

export default Slider;
